import { useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { Badge, Button, Input, Spinner, Stub } from "@/components/ui"
import { timeAgo } from "@/lib/utils"
import type { ConversationOut } from "@/lib/types"
import { ArchiveIcon, ArchiveRestoreIcon, PencilIcon, PinIcon, PinOffIcon, SearchIcon, Trash2Icon } from "lucide-react"

export const Route = createFileRoute("/_app/conversations")({
  component: ConversationsPage,
})

function ConversationsPage() {
  const t = useT()
  const me = auth.me()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [archived, setArchived] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draft, setDraft] = useState("")
  const list = useQuery({
    queryKey: ["conversations", me?.id, q, archived],
    queryFn: () => api<ConversationOut[]>(
      `/api/dashboard/users/${me?.id}/conversations`,
      { query: { q: q || undefined, limit: 100, archived: archived || undefined } },
    ),
    enabled: !!me?.id,
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ["conversations"] })

  const del = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/conversations/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  })
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { title?: string; pinned?: boolean; archived?: boolean } }) =>
      api(`/api/dashboard/conversations/${id}`, { method: "PATCH", body }),
    onSuccess: () => { refresh(); setRenaming(null) },
  })

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("conv.title")}</h1>
          <p className="text-[13px] text-muted-foreground mt-1.5">{t("conv.subtitle")}</p>
        </div>
        <Button variant={archived ? "default" : "outline"} size="sm" onClick={() => setArchived((a) => !a)}>
          <ArchiveIcon className="h-3.5 w-3.5" /> {t("conv.showArchived")}
        </Button>
      </div>

      <div className="flex items-center gap-3 rounded-[13px] border border-border bg-foreground/[0.04] px-4 backdrop-blur">
        <SearchIcon className="h-[17px] w-[17px] text-muted-foreground" />
        <input
          placeholder={t("conv.search")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-[46px] flex-1 border-none bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        <span className="hidden rounded-md border border-border px-1.5 py-0.5 text-[11px] text-[#5d6079] md:inline">⌘K</span>
      </div>

      {list.isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
      {list.data?.length === 0 && <Stub title={t("conv.empty")} hint={t("conv.emptyHint")} />}

      <div className="flex flex-col gap-2.5">
        {list.data?.map((c) => (
          <div
            key={c.id}
            onClick={() => renaming !== c.id && nav({ to: "/chat", search: { c: c.id } })}
            className="group flex cursor-pointer items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3.5 transition hover:border-primary/25 hover:bg-foreground/[0.06]"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {c.pinned && <PinIcon className="h-3.5 w-3.5 shrink-0 text-gold" />}
                {c.active_turn && (
                  <span className={`h-2 w-2 shrink-0 rounded-full ${c.active_turn.status === "running" ? "animate-pulse bg-primary" : "bg-muted-foreground/70"}`} />
                )}
                {renaming === c.id ? (
                  <form
                    className="flex flex-1 items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    onSubmit={(e) => { e.preventDefault(); patch.mutate({ id: c.id, body: { title: draft } }) }}
                  >
                    <Input value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8" autoFocus
                      onKeyDown={(e) => { if (e.key === "Escape") setRenaming(null) }} />
                    <Button size="sm" type="submit" disabled={patch.isPending}>{patch.isPending ? <Spinner /> : null}OK</Button>
                  </form>
                ) : (
                  <span className="truncate text-[14.5px] font-medium text-foreground">
                    {c.title || t("chat.untitled")}
                  </span>
                )}
                {c.tags?.map((tag) => <Badge key={tag} variant="muted">{tag}</Badge>)}
              </div>
              <p className="mt-1 truncate text-[13px] text-muted-foreground">
                {c.message_count ?? 0} {t("conv.messages")} · {t("conv.lastActive")} {timeAgo(c.last_message_at || c.updated_at)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-0.5 md:opacity-0 md:transition md:group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" title={t("conv.rename")} onClick={() => { setDraft(c.title || ""); setRenaming(c.id) }}>
                <PencilIcon className="h-4 w-4 text-muted-foreground" />
              </Button>
              {!c.archived_at && (
                <Button variant="ghost" size="icon" title={c.pinned ? t("conv.unpin") : t("conv.pin")} onClick={() => patch.mutate({ id: c.id, body: { pinned: !c.pinned } })}>
                  {c.pinned ? <PinOffIcon className="h-4 w-4 text-muted-foreground" /> : <PinIcon className="h-4 w-4 text-muted-foreground" />}
                </Button>
              )}
              <Button variant="ghost" size="icon" title={c.archived_at ? t("conv.unarchive") : t("conv.archive")} onClick={() => patch.mutate({ id: c.id, body: { archived: !c.archived_at } })}>
                {c.archived_at ? <ArchiveRestoreIcon className="h-4 w-4 text-muted-foreground" /> : <ArchiveIcon className="h-4 w-4 text-muted-foreground" />}
              </Button>
              <Button variant="ghost" size="icon" title={t("conv.delete")} onClick={() => { if (window.confirm(`${t("conv.delete")}?`)) del.mutate(c.id) }}>
                <Trash2Icon className="h-4 w-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
